import type { SectionType } from "@baze/schema";
import { t } from "@/i18n/id";

type Item = Record<string, unknown>;

/** A content list the large workspace edits from its tree (limits mirror packages/schema sections). */
export interface ListSpec {
  /** Content key of the list, relative to the parent item. */
  path: string;
  label: string;
  max: number;
  titleOf: (item: Item) => string;
  blank: (count: number) => Item;
  child?: ListSpec;
}

const text = (key: string) => (item: Item) => (typeof item[key] === "string" ? (item[key] as string) : "");
const f = t.editor.fields;

const items = (label: string, key: string, max: number, blank: (n: number) => Item): ListSpec => ({ path: "items", label, max, titleOf: text(key), blank });

export const LIST_SPECS: Partial<Record<SectionType, ListSpec>> = {
  lessons: {
    path: "groups",
    label: f.lessonGroups,
    max: 12,
    titleOf: (g) => [text("label")(g), text("title")(g)].filter(Boolean).join(" · "),
    blank: (n) => ({ label: `Pertemuan ${n + 1}`, title: "Pertemuan baru", topics: [{ title: "Topik baru" }] }),
    child: { path: "topics", label: f.lessonTopics, max: 30, titleOf: text("title"), blank: () => ({ title: "Topik baru" }) },
  },
  menu: {
    path: "categories",
    label: f.categories,
    max: 12,
    titleOf: text("name"),
    blank: () => ({ name: "Kategori", items: [{ name: "Menu baru" }] }),
    child: items(f.menuItems, "name", 40, () => ({ name: "Menu baru" })),
  },
  pricing: { path: "plans", label: f.plans, max: 6, titleOf: text("name"), blank: () => ({ name: "Paket baru", features: ["Fitur utama"] }) },
  services: items(f.items, "title", 24, () => ({ title: "Layanan baru" })),
  products: items(f.items, "name", 48, () => ({ name: "Produk baru" })),
  faq: items(f.items, "question", 20, () => ({ question: "Pertanyaan baru", answer: "Jawaban singkat." })),
  testimonials: items(f.items, "name", 12, () => ({ quote: "Ulasan pelanggan.", name: "Nama pelanggan" })),
  team: items(f.people, "name", 12, () => ({ name: "Nama" })),
  timeline: items(f.steps, "title", 12, () => ({ title: "Langkah baru" })),
  events: items(f.events, "name", 6, () => ({ name: "Acara baru" })),
  picks: items(f.items, "name", 24, () => ({ name: "Produk baru", href: "https://example.com" })),
  links: items(f.links, "label", 20, () => ({ label: "Tautan baru", href: "https://example.com" })),
};

/**
 * The list item a content path points into, cut to the depth the workspace edits
 * ("groups.0.topics.1.points.2" → groups/0/topics/1). Null when it is not in a spec'd list.
 */
export function itemPath(type: SectionType, path: string[]): string[] | null {
  const spec = LIST_SPECS[type];
  if (!spec || path[0] !== spec.path || !/^\d+$/.test(path[1] ?? "")) return null;
  if (spec.child && path[2] === spec.child.path && /^\d+$/.test(path[3] ?? "")) return path.slice(0, 4);
  return path.slice(0, 2);
}
